<template>
	<div class="footers">
		<footer class="main-footer">
			<div class="row">
				<div class="columns column3">
					<h4>{{ defaults[locale].website.hotelName }}</h4>
					<span>U bent ook welkom bij</span>
					<div class="logo-row">
						<a
							class="logo2 fix-on-scroll bg"
							href="https://www.grandcafedebrink.nl"
							target="_blank"
							rel="noopener"
						>
							<img src="~/assets/images/debrink-footer.svg" loading="lazy" alt="Hotel Wesseling logo" />
						</a>
					</div>
				</div>
				<div class="columns column3 contact">
					<h4>{{ $t('contact') }}</h4>
					<p class="address">
						<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span>
						<span>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span>
						<span>{{ defaults[locale].website.country }}</span>
					</p>
					<p>
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon class="icon" icon="fal fa-phone" size="1x" />
							{{ defaults[locale].website.phone }}
						</a>
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon class="icon" icon="fal fa-envelope" size="1x" />
							{{ defaults[locale].website.email }}
						</a>
					</p>
					<social-media :socials="socials" />
				</div>
				<div class="columns column3">
					<h4>{{ $t('quickLinks') }}</h4>
					<p>
						<nuxt-link v-for="item in footermenu[locale]" :key="item.filename" :to="item.filename">
							{{ item.header }}
						</nuxt-link>
					</p>
				</div>
				<div class="columns column3">
					<h4>{{ $t('legal') }}</h4>
					<p>
						<nuxt-link v-for="item in legalfooter[locale]" :key="item.filename" :to="item.filename">
							{{ item.header }}
						</nuxt-link>
						<a v-if="defaults[locale].cookieConsent.published" href="#" @click.prevent="openConsent"
							>{{ $t('consentOpen') }}
						</a>
					</p>
				</div>
			</div>
		</footer>
		<footer class="author-footer">
			<div class="row">
				<div class="columns column12">
					<div class="border" />
					<div class="bottomfooter-row">
						<p>
							<a
								href="https://www.porterforhotels.com/hotel-websites"
								target="_blank"
								rel="noopener noreferrer"
								title="Hotel website design by Porter"
							>
								Hotel Website Design
							</a>
							by:
							<a href="https://porterforhotels.com" target="_blank">
								<img class="siteByLogo" :src="$t('siteByLogo')" loading="lazy" alt="" />
								Porter</a
							>
						</p>
					</div>
				</div>
			</div>
		</footer>
	</div>
</template>

<script setup>
const { data: footer } = await useWebsiteFetch('footermenu');
const { footermenu, legalfooter } = footer.value;
const { locale } = useI18n();

const { defaults, socials, fetchDefaults } = useDefaults();
const { openConsent } = useCookieConsent();

await fetchDefaults();
</script>

<style lang="scss" scoped>
.footers {
	color: var(--footer-color);
	background: var(--footer-background-color);

	a,
	span {
		color: var(--footer-color);
		text-decoration: none;
	}

	h3,
	h4 {
		color: var(--footer-color);
		letter-spacing: 1px;
	}

	h4 {
		font-family: var(--heading-font-family);
		font-size: 1.3em;
	}
}

.main-footer {
	p {
		font-size: 0.9em;
	}

	a,
	span {
		display: block;
	}

	a:hover {
		text-decoration: underline;
	}

	.contact a {
		color: var(--cta-color);

		svg {
			margin-right: 10px;
			color: #fff;
		}
	}

	.row {
		.columns {
			padding-left: 90px;

			&:first-child {
				padding-left: 15px;
			}
		}

		:deep(.socials) {
			a {
				margin: 0;
				display: flex;
				justify-content: start;
			}
		}
	}
}

.logo-row {
	display: flex;
	justify-content: left;
	align-items: center;
	gap: 20px;
}

.logo-row img {
	height: 150px;
	width: auto;
	object-fit: contain;
	margin-top: 20px;
}

.bottomfooter-row {
	max-width: 1680px;
	margin: 0 auto;
	display: flex;
	justify-content: center;

	.columns {
		display: flex;
		justify-content: center;
	}

	.menu {
		margin-left: auto;

		.divider {
			padding: 0 5px;
		}
	}
}

.author-footer {
	font-size: 0.9em;
	padding: 5px 0;
	text-align: center;
	width: 100%;

	.border {
		background: #fff;
		width: 100%;
		height: 1px;
	}

	img {
		max-width: 20px;
		margin: 0 1px 0 2px;
	}
}

@media (max-width: 1300px) {
	.main-footer {
		.row {
			.columns {
				padding-left: 40px;

				&:first-child {
					padding-left: 15px;
				}
			}
		}
	}
}

@media (max-width: 1100px) {
	.main-footer {
		.row {
			.columns {
				padding-left: 30px;

				&:first-child {
					padding-left: 15px;
				}
			}
		}
	}
}

@media (max-width: 1080px) {
	footer .columns {
		margin: 0 auto 20px;
	}

	.main-footer {
		.row {
			.columns {
				padding-left: 15px;

				&:first-child {
					padding-left: 15px;

					.logo-row {
						max-width: 50%;
					}
				}
			}
		}
	}
}

@media (max-width: 680px) {
	footer .socials {
		justify-content: start;
	}
}

@media (max-width: 480px) {
	footer {
		text-align: center;

		.socials {
			justify-content: center;
		}
	}

	.main-footer {
		:deep(.socials) {
			a {
				justify-content: center;
			}
		}
	}

	.main-footer .footer-logo {
		margin: 0 auto;
	}

	.logo-row {
		margin: auto;

		img {
			height: 140px;
		}
	}

	.bottomfooter-row {
		flex-flow: column-reverse wrap;
		align-items: center;

		.menu {
			margin: 20px auto;
		}
	}
}
</style>
